import { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { usePromptLibraries } from "@/hooks/usePromptLibrary";

interface PromptSelectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (priming: string) => void;
  templateTypeId: number | null;
  reportingFormatTypeId: number | null;
}

export const PromptSelectDialog = ({
  open,
  onOpenChange,
  onSelect,
  templateTypeId,
  reportingFormatTypeId,
}: PromptSelectDialogProps) => {
  const { data: promptLibraries = [], isLoading, isError } = usePromptLibraries();
  const [search, setSearch] = useState("");

  const filteredLibraries = useMemo(() => {
    const q = search.trim().toLowerCase();
    return promptLibraries.filter((pl) => {
      if (!pl.is_active) return false;
      if (templateTypeId != null && pl.template_type_id !== templateTypeId) return false;
      if (
        reportingFormatTypeId != null &&
        !pl.template_formatetype_ids?.includes(reportingFormatTypeId)
      )
        return false;
      if (q && !pl.name.toLowerCase().includes(q) && !(pl.priming ?? "").toLowerCase().includes(q))
        return false;
      return true;
    });
  }, [promptLibraries, templateTypeId, reportingFormatTypeId, search]);

  const handleSelect = (priming: string) => {
    onSelect(priming);
    setSearch("");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) setSearch(""); onOpenChange(o); }}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Select AI Instructions</DialogTitle>
          <DialogDescription>
            Choose a prompt template from the library for AI content generation.
          </DialogDescription>
        </DialogHeader>

        <Input
          placeholder="Search prompts..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="mt-1"
        />

        <div className="overflow-y-auto max-h-[420px] space-y-2 pr-1 mt-1">
          {isLoading && (
            <p className="py-6 text-center text-sm text-muted-foreground">Loading…</p>
          )}
          {isError && (
            <p className="py-6 text-center text-sm text-destructive">Failed to load prompts.</p>
          )}
          {!isLoading && !isError && filteredLibraries.length === 0 && (
            <p className="py-6 text-center text-sm text-muted-foreground">No prompts found.</p>
          )}
          {filteredLibraries.map((pl) => (
            <div
              key={pl.id}
              className="border rounded-lg p-3 cursor-pointer transition-colors hover:border-brand-teal"
              onClick={() => handleSelect(pl.priming ?? "")}
            >
              <p className="font-semibold text-sm">{pl.name}</p>
              {pl.priming && (
                <p className="text-xs text-muted-foreground italic mt-1 line-clamp-3">
                  {pl.priming}
                </p>
              )}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};
